/**
 * Local campaign-report translations. Campaign statuses are pre-established
 * platform vocabulary (New lead, Enriched, …) — never AI-generated — so the
 * report's fixed labels translate from this dictionary instead of costing an
 * n8n translation. English renders directly; the other six are overrides
 * applied over the English source block.
 *
 * Languages: EN · ES · FR · DE · PT · IT · CA (top-6 European + Catalan).
 */
import { COPY as CAMPAIGN_COPY, CAMPAIGN_STATUS_KEYS, OBJECTIVE_KEYS, translationTexts, type CampaignReport, type Translations } from './campaign-report';

export const CAMPAIGN_REPORT_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt', 'it', 'ca', 'sv', 'da', 'nb', 'fi', 'is', 'pl', 'ru', 'uk', 'el', 'ro', 'hu', 'cs', 'zh', 'ja', 'ko', 'hi', 'id', 'vi', 'tr', 'he', 'ms'] as const;
export type CampaignReportLanguage = (typeof CAMPAIGN_REPORT_LANGUAGES)[number];

const ES: Partial<Translations> = {
  'label.reportTitle': 'Informe de la campaña',
  'label.pipeline': 'Estado del pipeline',
  'label.directory': 'Directorio de leads',
  'label.campaign': 'Campaña',
  'label.captured': 'Datos a fecha de',
  'label.stage': 'Etapa',
  'label.noLeads': 'Todavía no se han añadido leads a esta campaña.',
  'label.confidential': 'Información confidencial',
  'label.poweredBy': 'Impulsado por Brainiacs AI Intelligence',
  'label.compiledFor': 'Preparado para',
  'label.reference': 'Doc ID',
  'label.leadCol': 'Lead',
  'label.roleCol': 'Cargo',
  'label.companyCol': 'Empresa',
  'label.statusCol': 'Estado',
  'label.locationCol': 'Ubicación',
  'label.employeesCol': 'Empleados',
  'stage.new_lead': 'Nuevos leads',
  'stage.enriching': 'Enriqueciendo',
  'stage.enriched': 'Enriquecidos',
  'stage.outreaching': 'En contacto',
  'stage.outreached': 'Contactados',
  'stage.engaged': 'Interesados',
  'stage.proposal': 'Propuesta',
  'stage.converted': 'Convertidos',
  'stage.discarded': 'Descartados',
  'campaignStatus.active': 'Activa',
  'campaignStatus.paused': 'Pausada',
  'campaignStatus.archived': 'Archivada',
  'objective.lead_generation': 'Generación de leads',
  'objective.appointment_booking': 'Reserva de citas',
  'objective.product_demo_requests': 'Solicitudes de demo',
  'objective.webinar_registration': 'Registro en webinar',
  'objective.free_trial_signups': 'Pruebas gratuitas',
  'objective.partnership_opportunities': 'Oportunidades de partnership',

};
const FR: Partial<Translations> = {
  'label.reportTitle': "Rapport de campagne",
  'label.pipeline': "État du pipeline",
  'label.directory': "Répertoire des leads",
  'label.campaign': "Campagne",
  'label.captured': "Données au",
  'label.stage': "Étape",
  'label.noLeads': "Aucun lead n'a encore été ajouté à cette campagne.",
  'label.confidential': "Information confidentielle",
  'label.poweredBy': "Propulsé par Brainiacs AI Intelligence",
  'label.compiledFor': "Préparé pour",
  'label.reference': "ID du document",
  'label.leadCol': "Lead",
  'label.roleCol': "Poste",
  'label.companyCol': "Entreprise",
  'label.statusCol': "Statut",
  'label.locationCol': 'Localisation',
  'label.employeesCol': 'Employés',
  'stage.new_lead': "Nouveaux leads",
  'stage.enriching': "Enrichissement",
  'stage.enriched': "Enrichis",
  'stage.outreaching': "Contact en cours",
  'stage.outreached': "Contactés",
  'stage.engaged': "Engagés",
  'stage.proposal': "Proposition",
  'stage.converted': "Converties",
  'stage.discarded': "Écartés",
  'campaignStatus.active': 'Active',
  'campaignStatus.paused': 'En pause',
  'campaignStatus.archived': 'Archivée',
  'objective.lead_generation': 'Génération de leads',
  'objective.appointment_booking': 'Prise de rendez-vous',
  'objective.product_demo_requests': 'Demandes de démo',
  'objective.webinar_registration': 'Inscription au webinaire',
  'objective.free_trial_signups': 'Essais gratuits',
  'objective.partnership_opportunities': 'Opportunités de partenariat',

};
const DE: Partial<Translations> = {
  'label.reportTitle': 'Kampagnenbericht',
  'label.pipeline': 'Pipeline-Status',
  'label.directory': 'Lead-Verzeichnis',
  'label.campaign': 'Kampagne',
  'label.captured': 'Datenstand',
  'label.stage': 'Phase',
  'label.noLeads': 'Für diese Kampagne wurden noch keine Leads hinzugefügt.',
  'label.confidential': 'Vertrauliche Informationen',
  'label.poweredBy': 'Bereitgestellt von Brainiacs AI Intelligence',
  'label.compiledFor': 'Erstellt für',
  'label.reference': 'Doc-ID',
  'label.leadCol': 'Lead',
  'label.roleCol': 'Position',
  'label.companyCol': 'Unternehmen',
  'label.statusCol': 'Status',
  'label.locationCol': 'Standort',
  'label.employeesCol': 'Mitarbeiter',
  'stage.new_lead': 'Neue Leads',
  'stage.enriching': 'Anreicherung läuft',
  'stage.enriched': 'Angereichert',
  'stage.outreaching': 'Ansprache läuft',
  'stage.outreached': 'Kontaktiert',
  'stage.engaged': 'Interessiert',
  'stage.proposal': 'Angebot',
  'stage.converted': 'Gewonnen',
  'stage.discarded': 'Verworfen',
  'campaignStatus.active': 'Aktiv',
  'campaignStatus.paused': 'Pausiert',
  'campaignStatus.archived': 'Archiviert',
  'objective.lead_generation': 'Lead-Generierung',
  'objective.appointment_booking': 'Terminbuchung',
  'objective.product_demo_requests': 'Produktdemo-Anfragen',
  'objective.webinar_registration': 'Webinar-Anmeldung',
  'objective.free_trial_signups': 'Kostenlose Testversionen',
  'objective.partnership_opportunities': 'Partnerschaftsmöglichkeiten',

};
const PT: Partial<Translations> = {
  'label.reportTitle': 'Relatório de campanha',
  'label.pipeline': 'Estado do pipeline',
  'label.directory': 'Diretório de leads',
  'label.campaign': 'Campanha',
  'label.captured': 'Dados a partir de',
  'label.stage': 'Fase',
  'label.noLeads': 'Ainda não foram adicionados leads a esta campanha.',
  'label.confidential': 'Informação confidencial',
  'label.poweredBy': 'Desenvolvido por Brainiacs AI Intelligence',
  'label.compiledFor': 'Preparado para',
  'label.reference': 'ID do documento',
  'label.leadCol': 'Lead',
  'label.roleCol': 'Cargo',
  'label.companyCol': 'Empresa',
  'label.statusCol': 'Estado',
  'label.locationCol': 'Localização',
  'label.employeesCol': 'Funcionários',
  'stage.new_lead': 'Novos leads',
  'stage.enriching': 'A enriquecer',
  'stage.enriched': 'Enriquecidos',
  'stage.outreaching': 'A contactar',
  'stage.outreached': 'Contactados',
  'stage.engaged': 'Interessados',
  'stage.proposal': 'Proposta',
  'stage.converted': 'Convertidos',
  'stage.discarded': 'Descartados',
  'campaignStatus.active': 'Ativa',
  'campaignStatus.paused': 'Em pausa',
  'campaignStatus.archived': 'Arquivada',
  'objective.lead_generation': 'Geração de leads',
  'objective.appointment_booking': 'Marcação de reuniões',
  'objective.product_demo_requests': 'Pedidos de demonstração',
  'objective.webinar_registration': 'Inscrição em webinar',
  'objective.free_trial_signups': 'Testes gratuitos',
  'objective.partnership_opportunities': 'Oportunidades de parceria',

};
const IT: Partial<Translations> = {
  'label.reportTitle': 'Report della campagna',
  'label.pipeline': 'Stato della pipeline',
  'label.directory': 'Elenco dei lead',
  'label.campaign': 'Campagna',
  'label.captured': 'Dati al',
  'label.stage': 'Fase',
  'label.noLeads': 'Nessun lead è stato ancora aggiunto a questa campagna.',
  'label.confidential': 'Informazione riservata',
  'label.poweredBy': 'Offerto da Brainiacs AI Intelligence',
  'label.compiledFor': 'Preparato per',
  'label.reference': 'ID documento',
  'label.leadCol': 'Lead',
  'label.roleCol': 'Ruolo',
  'label.companyCol': 'Azienda',
  'label.statusCol': 'Stato',
  'label.locationCol': 'Sede',
  'label.employeesCol': 'Dipendenti',
  'stage.new_lead': 'Nuovi lead',
  'stage.enriching': 'Arricchimento',
  'stage.enriched': 'Arricchiti',
  'stage.outreaching': 'Contatto in corso',
  'stage.outreached': 'Contattati',
  'stage.engaged': 'Interessati',
  'stage.proposal': 'Proposta',
  'stage.converted': 'Convertiti',
  'stage.discarded': 'Scartati',
  'campaignStatus.active': 'Attiva',
  'campaignStatus.paused': 'In pausa',
  'campaignStatus.archived': 'Archiviata',
  'objective.lead_generation': 'Generazione di lead',
  'objective.appointment_booking': 'Prenotazione appuntamenti',
  'objective.product_demo_requests': 'Richieste di demo',
  'objective.webinar_registration': 'Iscrizione al webinar',
  'objective.free_trial_signups': 'Prove gratuite',
  'objective.partnership_opportunities': 'Opportunità di partnership',

};
const CA: Partial<Translations> = {
  'label.reportTitle': 'Informe de la campanya',
  'label.pipeline': 'Estat del pipeline',
  'label.directory': 'Directori de leads',
  'label.campaign': 'Campanya',
  'label.captured': 'Dades a data de',
  'label.stage': 'Fase',
  'label.noLeads': 'Encara no s\'han afegit leads a aquesta campanya.',
  'label.confidential': 'Informació confidencial',
  'label.poweredBy': 'Impulsat per Brainiacs AI Intelligence',
  'label.compiledFor': 'Preparat per a',
  'label.reference': 'ID del document',
  'label.leadCol': 'Lead',
  'label.roleCol': 'Càrrec',
  'label.companyCol': 'Empresa',
  'label.statusCol': 'Estat',
  'label.locationCol': 'Ubicació',
  'label.employeesCol': 'Empleats',
  'stage.new_lead': 'Leads nous',
  'stage.enriching': 'Enriquint',
  'stage.enriched': 'Enriquits',
  'stage.outreaching': 'En contacte',
  'stage.outreached': 'Contactats',
  'stage.engaged': 'Interessats',
  'stage.proposal': 'Proposta',
  'stage.converted': 'Convertits',
  'stage.discarded': 'Descartats',
  'campaignStatus.active': 'Activa',
  'campaignStatus.paused': 'Pausada',
  'campaignStatus.archived': 'Arxivada',
  'objective.lead_generation': 'Generació de leads',
  'objective.appointment_booking': 'Reserva de cites',
  'objective.product_demo_requests': 'Sol·licituds de demo',
  'objective.webinar_registration': 'Registre al webinar',
  'objective.free_trial_signups': 'Proves gratuïtes',
  'objective.partnership_opportunities': 'Oportunitats de partenariat',

};


// ── Nordics ──────────────────────────────────────────────────────────────────
const SV: Partial<Translations> = {
  'label.reportTitle': 'Kampanjrapport', 'label.pipeline': 'Pipeline-status', 'label.directory': 'Leadkatalog',
  'label.campaign': 'Kampanj', 'label.captured': 'Data per', 'label.stage': 'Fas',
  'label.noLeads': 'Inga leads har lagts till i denna kampanj ännu.',
  'label.confidential': 'Konfidentiell information', 'label.poweredBy': 'Drivs av Brainiacs AI Intelligence',
  'label.compiledFor': 'Sammanställd för', 'label.reference': 'Dok-ID',
  'label.leadCol': 'Lead', 'label.roleCol': 'Roll', 'label.companyCol': 'Företag', 'label.statusCol': 'Status', 'label.locationCol': 'Plats', 'label.employeesCol': 'Anställda',
  'stage.new_lead': 'Nya leads', 'stage.enriching': 'Anrikning pågår', 'stage.enriched': 'Anriksade',
  'stage.outreaching': 'Kontakt pågår', 'stage.outreached': 'Kontaktade', 'stage.engaged': 'Engagerade',
  'stage.proposal': 'Erbjudande', 'stage.converted': 'Konverterade', 'stage.discarded': 'Avvisade',
  'campaignStatus.active': 'Aktiv',
  'campaignStatus.paused': 'Pausad',
  'campaignStatus.archived': 'Arkiverad',
  'objective.lead_generation': 'Leadgenerering',
  'objective.appointment_booking': 'Mötesbokning',
  'objective.product_demo_requests': 'Produktdemoförfrågningar',
  'objective.webinar_registration': 'Webbinarregistrering',
  'objective.free_trial_signups': 'Kostnadsfria provperioder',
  'objective.partnership_opportunities': 'Partnerskapsmöjligheter',

};
const DA: Partial<Translations> = {
  'label.reportTitle': 'Kampagnerapport', 'label.pipeline': 'Pipeline-status', 'label.directory': 'Leadkatalog',
  'label.campaign': 'Kampagne', 'label.captured': 'Data pr.', 'label.stage': 'Fase',
  'label.noLeads': 'Der er endnu ikke blevet tilføjt leads til denne kampagne.',
  'label.confidential': 'Fortrolig information', 'label.poweredBy': 'Drevet af Brainiacs AI Intelligence',
  'label.compiledFor': 'Udarbejdet til', 'label.reference': 'Dok-ID',
  'label.leadCol': 'Lead', 'label.roleCol': 'Rolle', 'label.companyCol': 'Virksomhed', 'label.statusCol': 'Status', 'label.locationCol': 'Placering', 'label.employeesCol': 'Medarbejdere',
  'stage.new_lead': 'Nye leads', 'stage.enriching': 'Beriger', 'stage.enriched': 'Beriget',
  'stage.outreaching': 'Kontakter', 'stage.outreached': 'Kontaktet', 'stage.engaged': 'Engageret',
  'stage.proposal': 'Tilbud', 'stage.converted': 'Konverteret', 'stage.discarded': 'Kasserede',
  'campaignStatus.active': 'Aktiv',
  'campaignStatus.paused': 'Sat på pause',
  'campaignStatus.archived': 'Arkiveret',
  'objective.lead_generation': 'Leadgenerering',
  'objective.appointment_booking': 'Mødebooking',
  'objective.product_demo_requests': 'Anmodninger om produktdemo',
  'objective.webinar_registration': 'Webinartilmelding',
  'objective.free_trial_signups': 'Gratis prøveperioder',
  'objective.partnership_opportunities': 'Partnerskabsmuligheder',

};
const NB: Partial<Translations> = {
  'label.reportTitle': 'Kampanjerapport', 'label.pipeline': 'Pipeline-status', 'label.directory': 'Lead-katalog',
  'label.campaign': 'Kampanje', 'label.captured': 'Data per', 'label.stage': 'Fase',
  'label.noLeads': 'Ingen leads er lagt til denne kampanjen ennå.',
  'label.confidential': 'Konfidensiell informasjon', 'label.poweredBy': 'Drevet av Brainiacs AI Intelligence',
  'label.compiledFor': 'Utarbeidet for', 'label.reference': 'Dok-ID',
  'label.leadCol': 'Lead', 'label.roleCol': 'Rolle', 'label.companyCol': 'Selskap', 'label.statusCol': 'Status', 'label.locationCol': 'Beliggenhet', 'label.employeesCol': 'Ansatte',
  'stage.new_lead': 'Nye leads', 'stage.enriching': 'Beriker', 'stage.enriched': 'Beriket',
  'stage.outreaching': 'Kontakter', 'stage.outreached': 'Kontaktet', 'stage.engaged': 'Engasjert',
  'stage.proposal': 'Tilbud', 'stage.converted': 'Konvertert', 'stage.discarded': 'Avvist',
  'campaignStatus.active': 'Aktiv',
  'campaignStatus.paused': 'Satt på pause',
  'campaignStatus.archived': 'Arkivert',
  'objective.lead_generation': 'Leadgenerering',
  'objective.appointment_booking': 'Møtebooking',
  'objective.product_demo_requests': 'Forespørsler om produktdemo',
  'objective.webinar_registration': 'Webinarregistrering',
  'objective.free_trial_signups': 'Gratis prøveperioder',
  'objective.partnership_opportunities': 'Partnerskapsmuligheter',

};
const FI: Partial<Translations> = {
  'label.reportTitle': 'Kampanjaraportti', 'label.pipeline': 'Putken tila', 'label.directory': 'Lead-luettelo',
  'label.campaign': 'Kampanja', 'label.captured': 'Tiedot päivältä', 'label.stage': 'Vaihe',
  'label.noLeads': 'Tähän kampanjaan ei ole vielä lisätty liidejä.',
  'label.confidential': 'Luottamuksellista tietoa', 'label.poweredBy': 'Palvelun tarjoaa Brainiacs AI Intelligence',
  'label.compiledFor': 'Laatinut', 'label.reference': 'Asiakirjan tunnus',
  'label.leadCol': 'Liidi', 'label.roleCol': 'Tehtävä', 'label.companyCol': 'Yritys', 'label.statusCol': 'Tila', 'label.locationCol': 'Sijainti', 'label.employeesCol': 'Työntekijät',
  'stage.new_lead': 'Uudet liidit', 'stage.enriching': 'Rikastetaan', 'stage.enriched': 'Rikastettu',
  'stage.outreaching': 'Yhteydenotto käynnissä', 'stage.outreached': 'Yhteydenotettu', 'stage.engaged': 'Sitoutuneet',
  'stage.proposal': 'Tarjous', 'stage.converted': 'Muunnettu', 'stage.discarded': 'Hylätyt',
  'campaignStatus.active': 'Aktiivinen',
  'campaignStatus.paused': 'Keskeytetty',
  'campaignStatus.archived': 'Arkistoitu',
  'objective.lead_generation': 'Liidien hankinta',
  'objective.appointment_booking': 'Tapaamisten varaus',
  'objective.product_demo_requests': 'Tuotedemopyynnöt',
  'objective.webinar_registration': 'Webinaari-ilmoittautuminen',
  'objective.free_trial_signups': 'Ilmaiset kokeilut',
  'objective.partnership_opportunities': 'Kumppanuusmahdollisuudet',

};
const IS: Partial<Translations> = {
  'label.reportTitle': 'Herferðarskýrsla', 'label.pipeline': 'Staða ferils', 'label.directory': 'Skrá yfir viðskiptatengiliði',
  'label.campaign': 'Herferð', 'label.captured': 'Gögn eftir', 'label.stage': 'Stig',
  'label.noLeads': 'Engum viðskiptatengiliðum hefur enn verið bætt við þessa herferð.',
  'label.confidential': 'Trúnaðarupplýsingar', 'label.poweredBy': 'Keyrt af Brainiacs AI Intelligence',
  'label.compiledFor': 'Utgert fyrir', 'label.reference': 'Skjalkenni',
  'label.leadCol': 'Viðskiptatengiliður', 'label.roleCol': 'Staða', 'label.companyCol': 'Fyrirtæki', 'label.statusCol': 'Staða', 'label.locationCol': 'Staðsetning', 'label.employeesCol': 'Starfsmenn',
  'stage.new_lead': 'Nýjir viðskiptatengiliðir', 'stage.enriching': 'Í vinnslu', 'stage.enriched': 'Unnið',
  'stage.outreaching': 'Hafðir samband', 'stage.outreached': 'Hafðu samband', 'stage.engaged': 'Áhugahirðir',
  'stage.proposal': 'Tillaga', 'stage.converted': 'Umbreytt', 'stage.discarded': 'Höfnuð',
  'campaignStatus.active': 'Virk',
  'campaignStatus.paused': 'Í bið',
  'campaignStatus.archived': 'Geymt',
  'objective.lead_generation': 'Öflun viðskiptatengiliða',
  'objective.appointment_booking': 'Bókun funda',
  'objective.product_demo_requests': 'Beiðnir um vörusýn',
  'objective.webinar_registration': 'Skráning í vefnámskeið',
  'objective.free_trial_signups': 'Ókeypis prufur',
  'objective.partnership_opportunities': 'Samstarfstækifæri',

};

// ── Eastern Europe ───────────────────────────────────────────────────────────
const PL: Partial<Translations> = {
  'label.reportTitle': 'Raport kampanii', 'label.pipeline': 'Status lejka', 'label.directory': 'Katalog leadów',
  'label.campaign': 'Kampania', 'label.captured': 'Dane na dzień', 'label.stage': 'Etap',
  'label.noLeads': 'Do tej kampanii nie dodano jeszcze żadnych leadów.',
  'label.confidential': 'Informacja poufna', 'label.poweredBy': 'Obsługiwane przez Brainiacs AI Intelligence',
  'label.compiledFor': 'Sporządzono dla', 'label.reference': 'ID dokumentu',
  'label.leadCol': 'Lead', 'label.roleCol': 'Stanowisko', 'label.companyCol': 'Firma', 'label.statusCol': 'Status', 'label.locationCol': 'Lokalizacja', 'label.employeesCol': 'Pracownicy',
  'stage.new_lead': 'Nowe leady', 'stage.enriching': 'Wzbogacanie', 'stage.enriched': 'Wzbogacone',
  'stage.outreaching': 'Kontaktowanie', 'stage.outreached': 'Skontaktowani', 'stage.engaged': 'Zaangażowani',
  'stage.proposal': 'Oferta', 'stage.converted': 'Skonwertowani', 'stage.discarded': 'Odrzuceni',
  'campaignStatus.active': 'Aktywna',
  'campaignStatus.paused': 'Wstrzymana',
  'campaignStatus.archived': 'Zarchiwizowana',
  'objective.lead_generation': 'Generowanie leadów',
  'objective.appointment_booking': 'Rezerwacja spotkań',
  'objective.product_demo_requests': 'Prośby o demonstrację',
  'objective.webinar_registration': 'Rejestracja na webinar',
  'objective.free_trial_signups': 'Bezpłatne okresy próbne',
  'objective.partnership_opportunities': 'Możliwości partnerskie',

};
const RU: Partial<Translations> = {
  'label.reportTitle': 'Отчёт по кампании', 'label.pipeline': 'Статус воронки', 'label.directory': 'Каталог лидов',
  'label.campaign': 'Кампания', 'label.captured': 'Данные на', 'label.stage': 'Этап',
  'label.noLeads': 'В эту кампанию пока не добавлены лиды.',
  'label.confidential': 'Конфиденциальная информация', 'label.poweredBy': 'Работает на Brainiacs AI Intelligence',
  'label.compiledFor': 'Подготовлено для', 'label.reference': 'ID документа',
  'label.leadCol': 'Лид', 'label.roleCol': 'Должность', 'label.companyCol': 'Компания', 'label.statusCol': 'Статус', 'label.locationCol': 'Расположение', 'label.employeesCol': 'Сотрудники',
  'stage.new_lead': 'Новые лиды', 'stage.enriching': 'Обогащение', 'stage.enriched': 'Обогащённые',
  'stage.outreaching': 'Идёт контакт', 'stage.outreached': 'Связались', 'stage.engaged': 'Вовлечённые',
  'stage.proposal': 'Предложение', 'stage.converted': 'Конвертированные', 'stage.discarded': 'Отклонённые',
  'campaignStatus.active': 'Активна',
  'campaignStatus.paused': 'Приостановлена',
  'campaignStatus.archived': 'В архиве',
  'objective.lead_generation': 'Генерация лидов',
  'objective.appointment_booking': 'Запись на встречи',
  'objective.product_demo_requests': 'Запросы демонстрации',
  'objective.webinar_registration': 'Регистрация на вебинар',
  'objective.free_trial_signups': 'Бесплатные пробные периоды',
  'objective.partnership_opportunities': 'Партнёрские возможности',

};
const UK: Partial<Translations> = {
  'label.reportTitle': 'Звіт про кампанію', 'label.pipeline': 'Статус воронки', 'label.directory': 'Каталог лідів',
  'label.campaign': 'Кампанія', 'label.captured': 'Дані станом на', 'label.stage': 'Етап',
  'label.noLeads': 'До цієї кампанії ще не додано лідів.',
  'label.confidential': 'Конфіденційна інформація', 'label.poweredBy': 'Працює на Brainiacs AI Intelligence',
  'label.compiledFor': 'Підготовлено для', 'label.reference': 'ID документа',
  'label.leadCol': 'Лід', 'label.roleCol': 'Посада', 'label.companyCol': 'Компанія', 'label.statusCol': 'Статус', 'label.locationCol': 'Розташування', 'label.employeesCol': 'Співробітники',
  'stage.new_lead': 'Нові ліди', 'stage.enriching': 'Збагачення', 'stage.enriched': 'Збагачені',
  'stage.outreaching': 'Триває контакт', "stage.outreached": "Зв'язалися", 'stage.engaged': 'Залучені',
  'stage.proposal': 'Пропозиція', 'stage.converted': 'Конвертовані', 'stage.discarded': 'Відхилені',
  'campaignStatus.active': 'Активна',
  'campaignStatus.paused': 'Призупинена',
  'campaignStatus.archived': 'В архіві',
  'objective.lead_generation': 'Генерація лідів',
  'objective.appointment_booking': 'Запис на зустрічі',
  'objective.product_demo_requests': 'Запити на демонстрацію',
  'objective.webinar_registration': 'Реєстрація на вебінар',
  'objective.free_trial_signups': 'Безкоштовні пробні періоди',
  'objective.partnership_opportunities': 'Партнерські можливості',

};
const EL: Partial<Translations> = {
  'label.reportTitle': 'Αναφορά καμπάνιας', 'label.pipeline': 'Κατάσταση pipeline', 'label.directory': 'Κατάλογος leads',
  'label.campaign': 'Καμπάνια', 'label.captured': 'Δεδομένα per', 'label.stage': 'Στάδιο',
  'label.noLeads': 'Δεν έχουν προστεθεί ακόμη leads σε αυτή την καμπάνια.',
  'label.confidential': 'Εμπιστευτικές πληροφορίες', 'label.poweredBy': 'Με την υποστήριξη Brainiacs AI Intelligence',
  'label.compiledFor': 'Συντάχθηκε για', 'label.reference': 'Κωδικός εγγράφου',
  'label.leadCol': 'Lead', 'label.roleCol': 'Ρόλος', 'label.companyCol': 'Εταιρεία', 'label.statusCol': 'Κατάσταση', 'label.locationCol': 'Τοποθεσία', 'label.employeesCol': 'Υπάλληλοι',
  'stage.new_lead': 'Νέα leads', 'stage.enriching': 'Εμπλουτισμός', 'stage.enriched': 'Εμπλουτισμένα',
  'stage.outreaching': 'Επικοινωνία σε εξέλιξη', 'stage.outreached': 'Επικοινωνήσαμε', 'stage.engaged': 'Δέσμευση',
  'stage.proposal': 'Πρόταση', 'stage.converted': 'Μετατραπέντα', 'stage.discarded': 'Απορριφθέντα',
  'campaignStatus.active': 'Ενεργή',
  'campaignStatus.paused': 'Σε παύση',
  'campaignStatus.archived': 'Αρχειοθετημένη',
  'objective.lead_generation': 'Δημιουργία leads',
  'objective.appointment_booking': 'Κράτηση ραντεβού',
  'objective.product_demo_requests': 'Αιτήματα επίδειξης',
  'objective.webinar_registration': 'Εγγραφή σε webinar',
  'objective.free_trial_signups': 'Δωρεάν δοκιμές',
  'objective.partnership_opportunities': 'Ευκαιρίες συνεργασίας',

};
const RO: Partial<Translations> = {
  'label.reportTitle': 'Raport de campanie', 'label.pipeline': 'Stadiul pipeline', 'label.directory': 'Director de leaduri',
  'label.campaign': 'Campanie', 'label.captured': 'Date la', 'label.stage': 'Etapă',
  'label.noLeads': 'Nu au fost adăugați încă leaduri în această campanie.',
  'label.confidential': 'Informații confidențiale', 'label.poweredBy': 'Tehnologie Brainiacs AI Intelligence',
  'label.compiledFor': 'Elaborat pentru', 'label.reference': 'ID document',
  'label.leadCol': 'Lead', 'label.roleCol': 'Rol', 'label.companyCol': 'Companie', 'label.statusCol': 'Stare', 'label.locationCol': 'Locație', 'label.employeesCol': 'Angajați',
  'stage.new_lead': 'Leaduri noi', 'stage.enriching': 'Îmbogățire', 'stage.enriched': 'Îmbogățiți',
  'stage.outreaching': 'Contactare în curs', 'stage.outreached': 'Contactați', 'stage.engaged': 'Interesați',
  'stage.proposal': 'Ofertă', 'stage.converted': 'Convertiți', 'stage.discarded': 'Eliminați',
  'campaignStatus.active': 'Activă',
  'campaignStatus.paused': 'În pauză',
  'campaignStatus.archived': 'Arhivată',
  'objective.lead_generation': 'Generare de leaduri',
  'objective.appointment_booking': 'Programare întâlniri',
  'objective.product_demo_requests': 'Cereri de demonstrație',
  'objective.webinar_registration': 'Înscriere la webinar',
  'objective.free_trial_signups': 'Perioade de probă gratuite',
  'objective.partnership_opportunities': 'Oportunități de parteneriat',

};
const HU: Partial<Translations> = {
  'label.reportTitle': 'Kampányjelentés', 'label.pipeline': 'Pipeline állapota', 'label.directory': 'Lead-könyvtár',
  'label.campaign': 'Kampány', 'label.captured': 'Adatok ekkor:', 'label.stage': 'Szakasz',
  'label.noLeads': 'Még nem lettek leadok hozzáadva ehhez a kampányhoz.',
  'label.confidential': 'Bizalmas információ', 'label.poweredBy': 'Üzemelteti: Brainiacs AI Intelligence',
  'label.compiledFor': 'Készítve', 'label.reference': 'Dokumentumazonosító',
  'label.leadCol': 'Lead', 'label.roleCol': 'Pozíció', 'label.companyCol': 'Vállalat', 'label.statusCol': 'Állapot', 'label.locationCol': 'Helyszín', 'label.employeesCol': 'Alkalmazottak',
  'stage.new_lead': 'Új leadek', 'stage.enriching': 'Bővítés folyamatban', 'stage.enriched': 'Bővített',
  'stage.outreaching': 'Megkeresés folyamatban', 'stage.outreached': 'Megkeresve', 'stage.engaged': 'Elkötelezettek',
  'stage.proposal': 'Ajánlat', 'stage.converted': 'Konvertálva', 'stage.discarded': 'Elutasítva',
  'campaignStatus.active': 'Aktív',
  'campaignStatus.paused': 'Szüneteltetve',
  'campaignStatus.archived': 'Archivált',
  'objective.lead_generation': 'Lead-generálás',
  'objective.appointment_booking': 'Időpontfoglalás',
  'objective.product_demo_requests': 'Termékbemutató kérések',
  'objective.webinar_registration': 'Webinár-regisztráció',
  'objective.free_trial_signups': 'Ingyenes próbaidőszakok',
  'objective.partnership_opportunities': 'Partnerségi lehetőségek',

};
const CS: Partial<Translations> = {
  'label.reportTitle': 'Zpráva o kampani', 'label.pipeline': 'Stav pipeline', 'label.directory': 'Katalog leadů',
  'label.campaign': 'Kampaň', 'label.captured': 'Data k', 'label.stage': 'Fáze',
  'label.noLeads': 'K této kampani zatím nebyly přidány žádné leady.',
  'label.confidential': 'Důvěrné informace', 'label.poweredBy': 'Poháněno službou Brainiacs AI Intelligence',
  'label.compiledFor': 'Sestaveno pro', 'label.reference': 'ID dokumentu',
  'label.leadCol': 'Lead', 'label.roleCol': 'Pozice', 'label.companyCol': 'Společnost', 'label.statusCol': 'Stav', 'label.locationCol': 'Sídlo', 'label.employeesCol': 'Zaměstnanci',
  'stage.new_lead': 'Nové leady', 'stage.enriching': 'Obohacování', 'stage.enriched': 'Obohacené',
  'stage.outreaching': 'Kontaktování probíhá', 'stage.outreached': 'Kontaktováno', 'stage.engaged': 'Zapojené',
  'stage.proposal': 'Nabídka', 'stage.converted': 'Převedené', 'stage.discarded': 'Odmítnuté',
  'campaignStatus.active': 'Aktivní',
  'campaignStatus.paused': 'Pozastavena',
  'campaignStatus.archived': 'Archivovaná',
  'objective.lead_generation': 'Generování leadů',
  'objective.appointment_booking': 'Rezervace schůzek',
  'objective.product_demo_requests': 'Žádosti o ukázku',
  'objective.webinar_registration': 'Registrace na webinář',
  'objective.free_trial_signups': 'Bezplatné zkušební verze',
  'objective.partnership_opportunities': 'Partnerské příležitosti',

};

// ── Asia & Middle East ───────────────────────────────────────────────────────
const ZH: Partial<Translations> = {
  'label.reportTitle': '营销活动报告', 'label.pipeline': '销售管道状态', 'label.directory': '潜在客户名录',
  'label.campaign': '营销活动', 'label.captured': '数据截至', 'label.stage': '阶段',
  'label.noLeads': '此活动尚未添加潜在客户。',
  'label.confidential': '机密信息', 'label.poweredBy': '由 Brainiacs AI Intelligence 提供',
  'label.compiledFor': '编制对象', 'label.reference': '文档编号',
  'label.leadCol': '潜在客户', 'label.roleCol': '职位', 'label.companyCol': '公司', 'label.statusCol': '状态', 'label.locationCol': '所在地', 'label.employeesCol': '员工数',
  'stage.new_lead': '新线索', 'stage.enriching': '数据完善中', 'stage.enriched': '已完善',
  'stage.outreaching': '外联中', 'stage.outreached': '已联系', 'stage.engaged': '已互动',
  'stage.proposal': '方案', 'stage.converted': '已转化', 'stage.discarded': '已剔除',
  'campaignStatus.active': '进行中',
  'campaignStatus.paused': '已暂停',
  'campaignStatus.archived': '已归档',
  'objective.lead_generation': '潜在客户开发',
  'objective.appointment_booking': '预约洽谈',
  'objective.product_demo_requests': '产品演示请求',
  'objective.webinar_registration': '网络研讨会报名',
  'objective.free_trial_signups': '免费试用注册',
  'objective.partnership_opportunities': '合作机会',

};
const JA: Partial<Translations> = {
  'label.reportTitle': 'キャンペーンレポート', 'label.pipeline': 'パイプラインの状況', 'label.directory': 'リード名簿',
  'label.campaign': 'キャンペーン', 'label.captured': 'データ基準日', 'label.stage': 'ステージ',
  'label.noLeads': 'このキャンペーンにはまだリードが追加されていません。',
  'label.confidential': '機密情報', 'label.poweredBy': 'Powered by Brainiacs AI Intelligence',
  'label.compiledFor': '作成先', 'label.reference': 'ドキュメントID',
  'label.leadCol': 'リード', 'label.roleCol': '役職', 'label.companyCol': '会社名', 'label.statusCol': 'ステータス', 'label.locationCol': '所在地', 'label.employeesCol': '従業員数',
  'stage.new_lead': '新規リード', 'stage.enriching': 'データ拡張中', 'stage.enriched': '拡張済み',
  'stage.outreaching': 'アプローチ中', 'stage.outreached': 'アプローチ済み', 'stage.engaged': 'Engaged',
  'stage.proposal': '提案', 'stage.converted': '成約', 'stage.discarded': '除外',
  'campaignStatus.active': '進行中',
  'campaignStatus.paused': '一時停止',
  'campaignStatus.archived': 'アーカイブ済み',
  'objective.lead_generation': 'リード獲得',
  'objective.appointment_booking': 'アポイント予約',
  'objective.product_demo_requests': '製品デモ依頼',
  'objective.webinar_registration': 'ウェビナー登録',
  'objective.free_trial_signups': '無料トライアル登録',
  'objective.partnership_opportunities': 'パートナーシップ機会',

};
const KO: Partial<Translations> = {
  'label.reportTitle': '캠페인 보고서', 'label.pipeline': '파이프라인 현황', 'label.directory': '리드 디렉터리',
  'label.campaign': '캠페인', 'label.captured': '기준일', 'label.stage': '단계',
  'label.noLeads': '이 캠페인에 아직 리드가 추가되지 않았습니다.',
  'label.confidential': '기밀 정보', 'label.poweredBy': 'Powered by Brainiacs AI Intelligence',
  'label.compiledFor': '작성 대상', 'label.reference': '문서 ID',
  'label.leadCol': '리드', 'label.roleCol': '직책', 'label.companyCol': '회사', 'label.statusCol': '상태', 'label.locationCol': '위치', 'label.employeesCol': '직원 수',
  'stage.new_lead': '신규 리드', 'stage.enriching': '데이터 보강 중', 'stage.enriched': '보강 완료',
  'stage.outreaching': '접촉 진행 중', 'stage.outreached': '접촉 완료', 'stage.engaged': 'Engaged',
  'stage.proposal': '제안', 'stage.converted': '전환', 'stage.discarded': '제외',
  'campaignStatus.active': '진행 중',
  'campaignStatus.paused': '일시중지',
  'campaignStatus.archived': '보관됨',
  'objective.lead_generation': '리드 생성',
  'objective.appointment_booking': '미팅 예약',
  'objective.product_demo_requests': '제품 데모 요청',
  'objective.webinar_registration': '웨비나 등록',
  'objective.free_trial_signups': '무료 체험 신청',
  'objective.partnership_opportunities': '파트너십 기회',

};
const HI: Partial<Translations> = {
  'label.reportTitle': 'अभियान रिपोर्ट', 'label.pipeline': 'पाइपलाइन स्थिति', 'label.directory': 'लीड निर्देशिका',
  'label.campaign': 'अभियान', 'label.captured': 'आंकड़े', 'label.stage': 'चरण',
  'label.noLeads': 'इस अभियान में अभी तक कोई लीड नहीं जोड़ा गया है।',
  'label.confidential': 'गोपनीय जानकारी', 'label.poweredBy': 'Brainiacs AI Intelligence द्वारा संचालित',
  'label.compiledFor': 'के लिए संकलित', 'label.reference': 'दस्तावेज़ आईडी',
  'label.leadCol': 'लीड', 'label.roleCol': 'पद', 'label.companyCol': 'कंपनी', 'label.statusCol': 'स्थिति', 'label.locationCol': 'स्थान', 'label.employeesCol': 'कर्मचारी',
  'stage.new_lead': 'नए लीड', 'stage.enriching': 'संवर्धन जारी', 'stage.enriched': 'संवर्धित',
  'stage.outreaching': 'संपर्क जारी', 'stage.outreached': 'संपर्क किया', 'stage.engaged': 'जुड़े हुए',
  'stage.proposal': 'प्रस्ताव', 'stage.converted': 'रूपांतरित', 'stage.discarded': 'छोड़े गए',
  'campaignStatus.active': 'सक्रिय',
  'campaignStatus.paused': 'रोका गया',
  'campaignStatus.archived': 'संग्रहीत',
  'objective.lead_generation': 'लीड जनरेशन',
  'objective.appointment_booking': 'अपॉइंटमेंट बुकिंग',
  'objective.product_demo_requests': 'प्रोडक्ट डेमो अनुरोध',
  'objective.webinar_registration': 'वेबिनार पंजीकरण',
  'objective.free_trial_signups': 'निःशुल्क परीक्षण',
  'objective.partnership_opportunities': 'साझेदारी अवसर',

};
const ID: Partial<Translations> = {
  'label.reportTitle': 'Laporan kampanye', 'label.pipeline': 'Status pipeline', 'label.directory': 'Direktori prospek',
  'label.campaign': 'Kampanye', 'label.captured': 'Data per', 'label.stage': 'Tahap',
  'label.noLeads': 'Belum ada prospek yang ditambahkan ke kampanye ini.',
  'label.confidential': 'Informasi rahasia', 'label.poweredBy': 'Didukung oleh Brainiacs AI Intelligence',
  'label.compiledFor': 'Disusun untuk', 'label.reference': 'ID Dokumen',
  'label.leadCol': 'Prospek', 'label.roleCol': 'Jabatan', 'label.companyCol': 'Perusahaan', 'label.statusCol': 'Status', 'label.locationCol': 'Lokasi', 'label.employeesCol': 'Karyawan',
  'stage.new_lead': 'Prospek baru', 'stage.enriching': 'Memperkaya data', 'stage.enriched': 'Data diperkaya',
  'stage.outreaching': 'Sedang menghubungi', 'stage.outreached': 'Telah dihubungi', 'stage.engaged': 'Terlibat',
  'stage.proposal': 'Proposal', 'stage.converted': 'Terkonversi', 'stage.discarded': 'Disingkirkan',
  'campaignStatus.active': 'Aktif',
  'campaignStatus.paused': 'Dijeda',
  'campaignStatus.archived': 'Diarsipkan',
  'objective.lead_generation': 'Pembuatan prospek',
  'objective.appointment_booking': 'Pemesanan janji temu',
  'objective.product_demo_requests': 'Permintaan demo produk',
  'objective.webinar_registration': 'Pendaftaran webinar',
  'objective.free_trial_signups': 'Uji coba gratis',
  'objective.partnership_opportunities': 'Peluang kemitraan',

};
const VI: Partial<Translations> = {
  'label.reportTitle': 'Báo cáo chiến dịch', 'label.pipeline': 'Trạng thái quy trình', 'label.directory': 'Danh sách khách hàng tiềm năng',
  'label.campaign': 'Chiến dịch', 'label.captured': 'Dữ liệu tính đến', 'label.stage': 'Giai đoạn',
  'label.noLeads': 'Chưa có khách hàng tiềm năng nào được thêm vào chiến dịch này.',
  'label.confidential': 'Thông tin mật', 'label.poweredBy': 'Được vận hành bởi Brainiacs AI Intelligence',
  'label.compiledFor': 'Biên soạn cho', 'label.reference': 'Mã tài liệu',
  'label.leadCol': 'Khách hàng tiềm năng', 'label.roleCol': 'Chức vụ', 'label.companyCol': 'Công ty', 'label.statusCol': 'Trạng thái', 'label.locationCol': 'Vị trí', 'label.employeesCol': 'Nhân viên',
  'stage.new_lead': 'Mới', 'stage.enriching': 'Đang làm giàu dữ liệu', 'stage.enriched': 'Đã làm giàu dữ liệu',
  'stage.outreaching': 'Đang tiếp cận', 'stage.outreached': 'Đã tiếp cận', 'stage.engaged': 'Đã tương tác',
  'stage.proposal': 'Đề xuất', 'stage.converted': 'Đã chuyển đổi', 'stage.discarded': 'Đã loại bỏ',
  'campaignStatus.active': 'Đang hoạt động',
  'campaignStatus.paused': 'Tạm dừng',
  'campaignStatus.archived': 'Đã lưu trữ',
  'objective.lead_generation': 'Tạo khách hàng tiềm năng',
  'objective.appointment_booking': 'Đặt lịch hẹn',
  'objective.product_demo_requests': 'Yêu cầu demo sản phẩm',
  'objective.webinar_registration': 'Đăng ký webinar',
  'objective.free_trial_signups': 'Dùng thử miễn phí',
  'objective.partnership_opportunities': 'Cơ hội hợp tác',

};
const TR: Partial<Translations> = {
  'label.reportTitle': 'Kampanya raporu', 'label.pipeline': 'Aşamaların durumu', 'label.directory': 'Aday dizini',
  'label.campaign': 'Kampanya', 'label.captured': 'Veri tarihi', 'label.stage': 'Aşama',
  'label.noLeads': 'Bu kampanyaya henüz aday eklenmedi.',
  'label.confidential': 'Gizli bilgi', 'label.poweredBy': 'Brainiacs AI Intelligence ile güçlendirilmiştir',
  'label.compiledFor': 'Hazırlayan', 'label.reference': 'Belge kimliği',
  'label.leadCol': 'Aday', 'label.roleCol': 'Ünvan', 'label.companyCol': 'Şirket', 'label.statusCol': 'Durum', 'label.locationCol': 'Konum', 'label.employeesCol': 'Çalışanlar',
  'stage.new_lead': 'Yeni adaylar', 'stage.enriching': 'Zenginleştiriliyor', 'stage.enriched': 'Zenginleştirildi',
  'stage.outreaching': 'İletişim kuruluyor', 'stage.outreached': 'İletişim kuruldu', 'stage.engaged': 'İlgileniyor',
  'stage.proposal': 'Teklif', 'stage.converted': 'Dönüştü', 'stage.discarded': 'Elendi',
  'campaignStatus.active': 'Aktif',
  'campaignStatus.paused': 'Duraklatıldı',
  'campaignStatus.archived': 'Arşivlendi',
  'objective.lead_generation': 'Aday oluşturma',
  'objective.appointment_booking': 'Randevu alma',
  'objective.product_demo_requests': 'Ürün demo talepleri',
  'objective.webinar_registration': 'Webinar kaydı',
  'objective.free_trial_signups': 'Ücretsiz denemeler',
  'objective.partnership_opportunities': 'Ortaklık fırsatları',

};
const HE: Partial<Translations> = {
  'label.reportTitle': 'דוח קמפיין', 'label.pipeline': 'מצב הpipeline', 'label.directory': 'מאגר לידים',
  'label.campaign': 'קמפיין', 'label.captured': 'נתונים נכון ל', 'label.stage': 'שלב',
  'label.noLeads': 'טרם נוספו לידים לקמפיין זה.',
  'label.confidential': 'מידע חסוי', 'label.poweredBy': 'מופעל על ידי Brainiacs AI Intelligence',
  'label.compiledFor': 'הופק עבור', 'label.reference': 'מזהה מסמך',
  'label.leadCol': 'ליד', 'label.roleCol': 'תפקיד', 'label.companyCol': 'חברה', 'label.statusCol': 'סטטוס', 'label.locationCol': 'מיקום', 'label.employeesCol': 'עובדים',
  'stage.new_lead': 'לידים חדשים', 'stage.enriching': 'מעשיר נתונים', 'stage.enriched': 'נתונים הועשרו',
  'stage.outreaching': 'יוצר קשר', 'stage.outreached': 'נוצר קשר', 'stage.engaged': 'מעורב',
  'stage.proposal': 'הצעה', 'stage.converted': 'הומר', 'stage.discarded': 'נשלל',
  'campaignStatus.active': 'פעיל',
  'campaignStatus.paused': 'מושהה',
  'campaignStatus.archived': 'בארכיון',
  'objective.lead_generation': 'יצירת לידים',
  'objective.appointment_booking': 'קביעת פגישות',
  'objective.product_demo_requests': 'בקשות להדגמת מוצר',
  'objective.webinar_registration': 'הרשמה לוובינר',
  'objective.free_trial_signups': 'תקופות ניסיון חינם',
  'objective.partnership_opportunities': 'הזדמנויות שותפות',

};
const MS: Partial<Translations> = {
  'label.reportTitle': 'Laporan kempen', 'label.pipeline': 'Status saluran', 'label.directory': 'Direktori prospek',
  'label.campaign': 'Kempen', 'label.captured': 'Data pada', 'label.stage': 'Peringkat',
  'label.noLeads': 'Belum ada prospek ditambahkan ke kempen ini.',
  'label.confidential': 'Maklumat sulit', 'label.poweredBy': 'Dikuasakan oleh Brainiacs AI Intelligence',
  'label.compiledFor': 'Disediakan untuk', 'label.reference': 'ID Dokumen',
  'label.leadCol': 'Prospek', 'label.roleCol': 'Jawatan', 'label.companyCol': 'Syarikat', 'label.statusCol': 'Status', 'label.locationCol': 'Lokasi', 'label.employeesCol': 'Pekerja',
  'stage.new_lead': 'Prospek baharu', 'stage.enriching': 'Pengayaan data', 'stage.enriched': 'Data diperkaya',
  'stage.outreaching': 'Sedang dihubungi', 'stage.outreached': 'Telah dihubungi', 'stage.engaged': 'Terlibat',
  'stage.proposal': 'Cadangan', 'stage.converted': 'Ditukar', 'stage.discarded': 'Disingkirkan',
  'campaignStatus.active': 'Aktif',
  'campaignStatus.paused': 'Dijeda',
  'campaignStatus.archived': 'Diarkibkan',
  'objective.lead_generation': 'Penjanaan prospek',
  'objective.appointment_booking': 'Tempahan janji temu',
  'objective.product_demo_requests': 'Permintaan demo produk',
  'objective.webinar_registration': 'Pendaftaran webinar',
  'objective.free_trial_signups': 'Percubaan percuma',
  'objective.partnership_opportunities': 'Peluang perkongsian',

};

const DICTIONARY: Record<string, Partial<Translations>> = {
  es: ES, fr: FR, de: DE, pt: PT, it: IT, ca: CA,
  sv: SV, da: DA, nb: NB, fi: FI, is: IS,
  pl: PL, ru: RU, uk: UK, el: EL, ro: RO, hu: HU, cs: CS,
  zh: ZH, ja: JA, ko: KO, hi: HI, id: ID, vi: VI, tr: TR, he: HE, ms: MS,
};

/** Campaign status keys that MUST have a translation in every language. */
export const REQUIRED_STAGE_KEYS = ['new_lead','enriching','enriched','outreaching','outreached','engaged','proposal','converted','discarded'] as const;

/** Campaign-level status (CampaignStatusSelect) — closed vocabulary. */
export const REQUIRED_CAMPAIGN_STATUS_KEYS = CAMPAIGN_STATUS_KEYS;
/** Campaign objectives (CampaignForm) — closed vocabulary. */
export const REQUIRED_OBJECTIVE_KEYS = OBJECTIVE_KEYS;

/** Every key this dictionary is expected to override, in the exact form the
 *  template EMITS them. `translationTexts` owns the naming — importing the
 *  vocabularies instead of re-typing them is what keeps the two in sync. */
const REQUIRED_KEYS = Object.keys(CAMPAIGN_COPY).map(k => `label.${k}`)
  .concat(REQUIRED_STAGE_KEYS.map(k => `stage.${k}`))
  .concat(REQUIRED_CAMPAIGN_STATUS_KEYS.map(k => `campaignStatus.${k}`))
  .concat(REQUIRED_OBJECTIVE_KEYS.map(k => `objective.${k}`));

/** Validate at module load: every language covers every fixed label + stage. */
for (const [lang, overrides] of Object.entries(DICTIONARY)) {
  const missing = REQUIRED_KEYS.filter(key => !(key in overrides));
  if (missing.length) {
    throw new Error(`Campaign translation dictionary for "${lang}" is missing keys: ${missing.join(', ')}`);
  }
}

/**
 * Resolve the translations block for a language, filtered to the keys the
 * template emits. Several languages carry `nl`/`ar` entries that nothing emits —
 * those must NOT reach the preview, where `setTranslations` fills every key and
 * a language switch clears it again (see ReportPreviewManager). 'en' returns `{}`
 * so the English source renders as-is.
 */
export function getCampaignTranslations(language: string): Partial<Translations> {
  const base = String(language || 'en').slice(0, 2).toLowerCase();
  const overrides = (DICTIONARY as Record<string, Partial<Translations>>)[base];
  if (!overrides) return {};
  const out: Translations = {};
  for (const key of REQUIRED_KEYS) if (key in overrides) out[key] = overrides[key]!;
  return out;
}

/** True when this language needs no AI call — the dictionary covers it. */
export const hasCampaignTranslations = (language: string): boolean =>
  Object.keys(getCampaignTranslations(language)).length > 0;

/**
 * The COMPLETE translations block for one language: the report's own source
 * texts with the dictionary layered over them.
 *
 * This is the ONLY correct way to build it. The template's `validateTranslations`
 * requires the translated block to hold EXACTLY the keys `translationTexts`
 * emits, so the dictionary must be narrowed to THOSE keys — not merely to the
 * full vocabulary. A campaign has one status, one objective and a slice of the
 * stages, and an entry for anything it does not have is an EXTRA key that makes
 * validation throw ("Translation is incomplete"). English returns `undefined`
 * so the source renders as-is.
 */
export function buildCampaignTranslations(
  report: CampaignReport,
  language: string,
  source?: Translations
): { translations: Translations | undefined; fromDictionary: boolean } {
  const base = source ?? translationTexts(report);
  const translations: Translations = { ...base };
  let fromDictionary = false;
  for (const [key, value] of Object.entries(getCampaignTranslations(language))) {
    // Only override a key this report actually emits.
    if (Object.hasOwn(base, key)) { translations[key] = value; fromDictionary = true; }
  }
  return { translations: fromDictionary ? translations : undefined, fromDictionary };
}
